import PropTypes from "prop-types"
import React, { useState } from "react"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import timezone from "dayjs/plugin/timezone"
import { Form, Input, FormFeedback, Label } from "reactstrap"

import LoadingButton from "../../components/Common/LoadingButton"
import AuthLayout from "../../components/Common/AuthLayout"
import PasswordInput from "../../components/Common/PasswordInput"

import { Link } from "react-router-dom"
import withRouter from "../../components/Common/withRouter"

import { useDispatch } from "react-redux"
import { toast, Bounce } from "react-toastify"
import { post } from "../../api/axios"
import endpoints from "../../api/endpoints"
import { setLogin } from "../../store/slices/authSlice"
import { getAuthenticatedHomePath } from "../../helpers/routeAccess"
import { toastApiError } from "../../helpers/apiError"
import * as Yup from "yup"
import { useFormik } from "formik"

dayjs.extend(utc)
dayjs.extend(timezone)

const Login = props => {
  document.title = " Login | AMOK . EARTH"

  const dispatch = useDispatch()
  const navigate = props.router?.navigate
  const [isSubmitting, setIsSubmitting] = useState(false)

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email("Invalid email address")
        .required("Please enter your email"),
      password: Yup.string().required("Please enter your password"),
    }),
    onSubmit: async values => {
      const fcmToken = localStorage.getItem("fcm")
      if (isSubmitting) return
      setIsSubmitting(true)
      try {
        const response = await post(endpoints.login, {
          email: values.email,
          password: values.password,
          timezone: dayjs.tz.guess(),
          fcm_token: fcmToken,
          device_id: fcmToken,
          remember_me: true,
        })

        const result = response?.data?.result
        const user = result?.user
        const needsTwoFactor =
          Boolean(result?.two_factor_required) ||
          Boolean(user?.two_factor_enabled) ||
          Boolean(user?.is_two_factor_enabled)

        if (response?.data?.success && needsTwoFactor) {
          toast.success(
            response?.data?.message ||
              "Enter the verification code to continue.",
            {
              position: "bottom-center",
              theme: "colored",
              transition: Bounce,
            },
          )
          navigate("/verify-otp", {
            state: {
              email: values.email,
              otpType: "2fa-verification",
              pendingToken: result?.token || null,
              pendingUser: user || null,
            },
            replace: true,
          })
          return
        }

        if (response?.data?.success && result?.token) {
          dispatch(
            setLogin({
              currentUser: user,
              jid: result.token,
              isAuth: true,
              loginType: "credentials",
            }),
          )
          toast.success(response?.data?.message, {
            position: "bottom-center",
            theme: "colored",
            transition: Bounce,
          })
          navigate(getAuthenticatedHomePath(user), {
            replace: true,
          })
        } else if (
          response?.data?.success &&
          result?.email_verified === false
        ) {
          navigate("/verify-email", { state: { email: values.email } })
          toast.success(
            `${response?.data?.message}, please verify your email`,
            {
              position: "bottom-center",
              theme: "colored",
              transition: Bounce,
            },
          )
        } else if (response) {
          toastApiError(response, "Invalid email or password.")
        }
      } catch (error) {
        toastApiError(error)
      } finally {
        setIsSubmitting(false)
      }
    },
  })

  return (
    <AuthLayout
      title="Welcome back"
      subtitle="Sign in to continue your projects"
    >
      <Form
        className="auth-page__form"
        onSubmit={e => {
          e.preventDefault()
          validation.handleSubmit()
          return false
        }}
      >
        <div className="auth-page__field">
          <Label className="auth-page__label">Email</Label>
          <Input
            name="email"
            className="form-control"
            placeholder="Enter your email address"
            type="email"
            onChange={validation.handleChange}
            onBlur={validation.handleBlur}
            value={validation.values.email || ""}
            invalid={!!(validation.touched.email && validation.errors.email)}
          />
          {validation.touched.email && validation.errors.email ? (
            <FormFeedback type="invalid">{validation.errors.email}</FormFeedback>
          ) : null}
        </div>

        <div className="auth-page__field">
          <Label className="auth-page__label">Password</Label>
          <PasswordInput
            name="password"
            value={validation.values.password || ""}
            placeholder="Enter Password"
            onChange={validation.handleChange}
            onBlur={validation.handleBlur}
            invalid={
              !!(validation.touched.password && validation.errors.password)
            }
          />
          {validation.touched.password && validation.errors.password ? (
            <FormFeedback type="invalid">
              {validation.errors.password}
            </FormFeedback>
          ) : null}
        </div>

        <div className="auth-page__forgot">
          <Link to="/forgot-password" className="auth-page__forgot-link">
            Forgot Password?
          </Link>
        </div>

        <LoadingButton loading={isSubmitting}>Sign In</LoadingButton>
      </Form>
    </AuthLayout>
  )
}

export default withRouter(Login)

Login.propTypes = {
  error: PropTypes.any,
  history: PropTypes.object,
  loginUser: PropTypes.func,
  socialLogin: PropTypes.func,
}
